/**
 * Admin: dekking prijsboek per hoofdgroep (10–71), live merged library.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layers, RefreshCw } from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { api } from '../../lib/api';

type CoverageGroup = Awaited<ReturnType<typeof api.getAdminLibraryCoverage>>['groups'][number];

function selectClass(disabled?: boolean) {
  return `w-full max-w-[260px] rounded-lg border border-border bg-surface-card px-2 py-2 text-xs text-text ${
    disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-primary/40'
  }`;
}

function GroupSystemVariantSelects({ group }: { group: CoverageGroup }) {
  const [systemId, setSystemId] = useState('');
  const [variantId, setVariantId] = useState('');
  const system = group.systems.find((s) => s.system_id === systemId);
  const variants = system?.variants ?? [];

  useEffect(() => {
    setVariantId('');
  }, [systemId]);

  return (
    <div className="flex min-w-[200px] max-w-[280px] flex-col gap-2">
      <div>
        <label className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-text-muted">
          Systeem
        </label>
        <select
          value={systemId}
          onChange={(e) => setSystemId(e.target.value)}
          className={selectClass()}
          aria-label={`Systeem kiezen in hoofdgroep ${group.group_code}`}
        >
          <option value="">— {group.system_count} systeem{group.system_count === 1 ? '' : 'en'} —</option>
          {group.systems.map((s) => (
            <option key={s.system_id} value={s.system_id}>
              {s.label} ({s.variant_count} var.)
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-text-muted">
          Variant
        </label>
        <select
          value={variantId}
          onChange={(e) => setVariantId(e.target.value)}
          disabled={!systemId}
          className={selectClass(!systemId)}
          aria-label={`Variant kiezen voor ${systemId || 'systeem'}`}
        >
          <option value="">
            {systemId ? `— ${variants.length} variant${variants.length === 1 ? '' : 'en'} —` : '— kies eerst systeem —'}
          </option>
          {variants.map((v) => (
            <option key={v.variant_id} value={v.variant_id}>
              {v.label}
            </option>
          ))}
        </select>
      </div>
      {systemId && system && (
        <p className="break-all font-mono text-[10px] leading-snug text-text-muted">
          {system.catalog_subgroup && (
            <>
              <span className="text-text-muted">subgroep:</span> {system.catalog_subgroup}
              <br />
            </>
          )}
          <span className="text-text-muted">id:</span> {systemId}
          {variantId && (
            <>
              <br />
              <span className="text-text-muted">variant:</span> {variantId}
            </>
          )}
        </p>
      )}
    </div>
  );
}

function fmtDate(iso: string | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('nl-NL', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export function AdminLibraryCoverage() {
  const { user } = useAuth();
  const tenantId = user?.tenant_id ?? 'default';

  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ['admin-library-coverage', tenantId],
    queryFn: () => api.getAdminLibraryCoverage({ tenant_id: tenantId }),
    enabled: !!user?.authenticated,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const errMsg = error instanceof Error ? error.message : String(error ?? '');
  const forbidden =
    errMsg.toLowerCase().includes('forbidden') || errMsg.toLowerCase().includes('beheerder');

  const totals = useMemo(() => {
    if (!data?.groups?.length) return null;
    let systems = 0;
    let variants = 0;
    for (const g of data.groups) {
      systems += g.system_count;
      variants += g.variant_count;
    }
    const avg = systems > 0 ? Math.round((variants / systems) * 100) / 100 : 0;
    return {
      hoofdgroepen: data.groups.length,
      systemen: systems,
      varianten: variants,
      gem_varianten_per_systeem: avg,
    };
  }, [data?.groups]);

  return (
    <div>
      <SeoMeta
        path="/admin/library-coverage"
        noindex
        title="Library-dekking"
        description="CalqTool™ prijsbibliotheek-dekking per hoofdgroep: systemen, varianten en live merge seed+D1 — intern platforminzicht."
      />
      <Breadcrumbs
        items={[{ label: 'Platformbeheer', href: '/admin' }, { label: 'Library-dekking' }]}
      />
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Layers className="h-6 w-6 text-primary" aria-hidden />
          <h1 className="text-2xl font-bold text-text">Dekking per hoofdgroep</h1>
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 self-start rounded-lg border border-border bg-surface-card px-3 py-2 text-sm font-medium text-text hover:bg-surface disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
          {isFetching ? 'Vernieuwen…' : 'Opnieuw laden'}
        </button>
      </div>

      <p className="mb-4 max-w-3xl text-sm text-text-muted">
        Live samenvatting van de <strong className="text-text">meest recente merged library</strong> (seed + tenant-D1)
        per hoofdgroep. Telt elke keer opnieuw bij laden of na deploy; versie en releasedatum komen uit de catalogus-manifest.
      </p>

      {forbidden && (
        <p className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-text">
          Geen toegang — alleen admin-accounts zien deze pagina.
        </p>
      )}

      {data && (
        <>
          <div className="mb-4 flex flex-wrap gap-4 rounded-xl border border-border bg-surface-card px-4 py-3 text-sm text-text-muted">
            <div>
              <span className="font-medium text-text">Libraryversie:</span>{' '}
              <code className="rounded bg-surface px-1.5 py-0.5 text-xs text-text">
                {data.catalog_version ?? '—'}
              </code>
            </div>
            <div>
              <span className="font-medium text-text">Release (manifest):</span> {fmtDate(data.catalog_released_at)}
            </div>
            <div>
              <span className="font-medium text-text">Snapshot:</span> {fmtDate(data.generated_at)}
            </div>
            <div>
              <span className="font-medium text-text">Tenant:</span> {data.tenant_id}
            </div>
          </div>
          {totals && (
            <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl border border-border bg-surface-card px-4 py-3 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Hoofdgroepen</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums text-text">{totals.hoofdgroepen}</p>
                <p className="mt-0.5 text-[11px] text-text-muted">Codes 10–71 (actief in library)</p>
              </div>
              <div className="rounded-xl border border-border bg-surface-card px-4 py-3 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Systemen (totaal)</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums text-text">{totals.systemen}</p>
                <p className="mt-0.5 text-[11px] text-text-muted">Actief, niet vervangen · over alle groepen</p>
              </div>
              <div className="rounded-xl border border-border bg-surface-card px-4 py-3 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Varianten (totaal)</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums text-text">{totals.varianten}</p>
                <p className="mt-0.5 text-[11px] text-text-muted">Actief · merge seed + tenant</p>
              </div>
              <div className="rounded-xl border border-border bg-surface-card px-4 py-3 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Gem. variant / systeem</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums text-text">
                  {totals.gem_varianten_per_systeem}
                </p>
                <p className="mt-0.5 text-[11px] text-text-muted">Over gehele merged catalogus</p>
              </div>
            </div>
          )}
        </>
      )}

      {isLoading && <p className="text-sm text-text-muted">Laden…</p>}
      {!isLoading && error && !forbidden && (
        <p className="text-sm text-red-600 dark:text-red-400">{errMsg}</p>
      )}

      {data && (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[920px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-surface">
                <th className="px-3 py-2.5 font-semibold text-text">Code</th>
                <th className="px-3 py-2.5 font-semibold text-text">Hoofdgroep</th>
                <th className="px-3 py-2.5 font-semibold text-text">Systemen</th>
                <th className="px-3 py-2.5 font-semibold text-text">Varianten</th>
                <th className="px-3 py-2.5 font-semibold text-text">Variantdiepte</th>
                <th className="px-3 py-2.5 font-semibold text-text">Systeem en variant</th>
                <th className="px-3 py-2.5 font-semibold text-text">Beoordeling</th>
              </tr>
            </thead>
            <tbody>
              {data.groups.map((g) => (
                <tr key={g.group_code} className="border-b border-border/80 align-top hover:bg-surface/60">
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono text-text">{g.group_code}</td>
                  <td className="max-w-[200px] px-3 py-2.5 text-text">{g.label_nl}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-text">{g.system_count}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-text-muted">
                    {g.variant_count}
                    <span className="ml-1 text-xs text-text-muted">
                      (ø {g.avg_variants_per_system}, max {g.max_variants_per_system})
                    </span>
                  </td>
                  <td className="max-w-[280px] px-3 py-2.5 text-text-muted">
                    {g.variant_diepte_editorial_nl && (
                      <p className="mb-1 text-text">{g.variant_diepte_editorial_nl}</p>
                    )}
                    <details className="text-xs">
                      <summary className="cursor-pointer select-none text-primary hover:underline">
                        Exacte verdeling (aantal varianten per systeem)
                      </summary>
                      <ul className="mt-1.5 list-inside list-disc space-y-0.5 text-text-muted">
                        {g.variant_depth_distribution.map((row) => (
                          <li key={row.depth}>
                            {row.system_count} syst. met {row.depth} variant{row.depth === 1 ? '' : 'en'}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-1 font-mono text-[11px] text-text-muted">{g.variant_diepte_histogram_nl}</p>
                    </details>
                  </td>
                  <td className="px-3 py-2.5 align-top">
                    <GroupSystemVariantSelects group={g} />
                  </td>
                  <td className="max-w-[240px] px-3 py-2.5 text-text">
                    {g.beoordeling_editorial_nl && (
                      <p className="font-medium">{g.beoordeling_editorial_nl}</p>
                    )}
                    <details className="mt-0.5 text-xs text-text-muted">
                      <summary className="cursor-pointer select-none text-primary hover:underline">
                        Automatische inschatting
                      </summary>
                      <p className="mt-1">{g.beoordeling_auto_nl}</p>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
